import { FC, Fragment } from 'react';
import {
  Box,
  Card,
  Chip,
  Table,
  Button,
  Collapse,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  CardHeader,
  IconButton,
  Typography,
  CardContent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import axios from '@/utils/api/axios';
import moment from 'moment';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import Iconify from '@components/media/iconify';
import Label from '@/components/core/label';
import { ShowIf } from '@utils/index';
import RHFNumberField from '@components/forms/hook-form/rhf-number-field';
import { OrderOVInformation } from '../../../orders/forms/components/order-ov-information';
import {
  IOrderDocument,
  ORDER_DOCUMENT_TYPES,
  ChoicesOrderItemLabel,
  IBillingOrderCurrentItem,
} from '@/types';
import {
  IMilestone,
  ChoicesOrderItemType,
  ChoicesOrderUnitMeasure,
} from '@/sections/administrative/accounts/orders/context';

interface Entity {
  _id: string;
  label: string;
  legal_name: string;
  tax_number: string;
  documents: IOrderDocument[];
}

interface BillingOrderLegalEntityBillingItemProps {
  legalEntity: Entity;
  legalIndex: number;
  itemsCurrentOrder: IBillingOrderCurrentItem[];
  watchAllFields: any;
  unitMesureChoicesHelpText: Record<ChoicesOrderUnitMeasure, string>;
  calculateAmount: (index: number, pos: number, value: number) => void;
  groupMilestonesBysubscription: (milestones: IMilestone[]) => any[];
  expandedRows: Record<string, string[]>;
  toggleRow: (legalEntityId: string, subscriptionId: string) => void;
  onAddDocument: (legalEntityId: string) => void;
  currentOrder: any;
  onDeleteDocument?: (documentId: string, legalEntityId: string) => void;
}

/**
 * Component for displaying billing information for a specific legal entity in billing orders
 * Extracted from billing-order-new-edit-secondstep-form.tsx to match order structure
 */
export const BillingOrderLegalEntityBillingItem: FC<BillingOrderLegalEntityBillingItemProps> = ({
  legalEntity,
  legalIndex,
  watchAllFields,
  unitMesureChoicesHelpText,
  calculateAmount,
  groupMilestonesBysubscription,
  expandedRows,
  toggleRow,
  currentOrder,
  onAddDocument,
  onDeleteDocument,
}) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const { enqueueSnackbar } = useSnackbar();

  const handleDeleteDocument = async (documentId: string) => {
    try {
      if (onDeleteDocument) {
        await onDeleteDocument(documentId, legalEntity._id);
      } else {
        if (documentId) {
          await axios.delete(`/order-documents/${documentId}`);
        }

        const currentDocuments = watchAllFields?.legalEntity?.[legalIndex]?.documents || [];
        const updatedDocuments = currentDocuments.filter(
          (doc: IOrderDocument) => doc._id !== documentId
        );

        setValue(`legalEntity[${legalIndex}].documents`, updatedDocuments, {
          shouldValidate: true,
        });

        enqueueSnackbar(t('Document deleted successfully'), {
          variant: 'success',
        });
      }
    } catch (err) {
      enqueueSnackbar(t('Error deleting document'), {
        variant: 'error',
      });
    }
  };

  const totalAmount =
    watchAllFields?.items?.[legalIndex]?.amount?.reduce(
      (sum: number, amount: number) => sum + (amount || 0),
      0
    ) || 0;

  const documents = watchAllFields?.legalEntity?.[legalIndex]?.documents || [];
  const purchaseOrderDocs = documents.filter(
    (doc: any) => doc?.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER
  );
  const purchaseOrderAmount = purchaseOrderDocs.reduce(
    (sum: number, doc: any) => sum + (parseFloat(doc?.amount) || 0),
    0
  );

  const isAmountMismatch =
    purchaseOrderDocs.length > 0 &&
    purchaseOrderAmount > 0 &&
    Math.abs(purchaseOrderAmount - totalAmount) > 0.01;

  return (
    <Card key={legalEntity._id} sx={{ mb: 2 }}>
      <CardHeader
        avatar={<Iconify icon="solar:buildings-2-bold-duotone" sx={{ color: 'primary.main' }} />}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{legalEntity.legal_name || legalEntity.label}</Typography>
            <Typography component="span" color="textSecondary" variant="body2">
              ({legalEntity.tax_number})
            </Typography>
            <Chip
              label={t('Legal Entity')}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="mingcute:add-line" />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAddDocument(legalEntity._id);
              }}
            >
              {watchAllFields?.legalEntity?.[legalIndex]?.documents?.length > 0
                ? t('Add Document')
                : t('Add Documents')}
            </Button>
          </Box>
        }
      />
      <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
        <Table stickyHeader aria-label="legal entities table" sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>{t('Subscription')}</TableCell>
              <TableCell align="center" />
              <TableCell align="center">{t('Quantity')}</TableCell>
              <TableCell align="center">{t('Amount')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupMilestonesBysubscription(currentOrder.selectedMilestones || []).map(
              ([subscriptionId, subscriptionGroup]) => {
                const { subscription, milestones } = subscriptionGroup;
                const productName = milestones[0]?.orderItem?.product?.name;
                const skuName = milestones[0]?.orderItem?.sku?.name;
                const planName = milestones[0]?.orderItem?.plan?.name;
                if (milestones[0]?.originalIndex === -1) {
                  return null;
                }

                return (
                  <Fragment key={subscriptionId}>
                    <TableRow key={`product-${subscriptionId}`}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {productName}-{skuName}-{planName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => toggleRow(legalEntity._id, subscriptionId)}>
                          {(expandedRows[legalEntity._id] || []).includes(subscriptionId) ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        <RHFNumberField
                          thousandsGroupStyle="thousand"
                          helperText={
                            unitMesureChoicesHelpText[
                              watchAllFields.unitMeasure[
                                milestones[0]?.originalIndex
                              ] as ChoicesOrderUnitMeasure
                            ]
                          }
                          inputProps={{ autoComplete: 'off' }}
                          allowNegative={false}
                          thousandSeparator
                          decimalScale={2}
                          label={t('Value to be billed')}
                          size="small"
                          suffix={
                            watchAllFields.unitMeasure[milestones[0]?.originalIndex] ===
                            ChoicesOrderUnitMeasure.percentageAmount
                              ? ' %'
                              : ''
                          }
                          prefix={
                            watchAllFields.unitMeasure[milestones[0]?.originalIndex] ===
                            ChoicesOrderUnitMeasure.amount
                              ? '$ '
                              : ''
                          }
                          sx={{ width: '60%' }}
                          name={`items[${legalIndex}].value[${milestones[0]?.originalIndex}]`}
                          onValueChange={(event) => {
                            const newValue = event.floatValue;
                            calculateAmount(
                              legalIndex,
                              milestones[0]?.originalIndex,
                              Number(newValue)
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <RHFNumberField
                          disabled
                          thousandsGroupStyle="thousand"
                          thousandSeparator
                          helperText=" "
                          label={t('Amount to bill')}
                          decimalScale={4}
                          prefix="$ "
                          sx={{ width: '60%' }}
                          size="small"
                          name={`items[${legalIndex}].amount[${milestones[0]?.originalIndex}]`}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow key={`product-details-${subscriptionId}`}>
                      <TableCell colSpan={4} style={{ padding: 0, border: 0 }}>
                        <Collapse
                          in={(expandedRows[legalEntity._id] || []).includes(subscriptionId)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ margin: 1 }}>
                            {milestones.map((milestone: any, milestoneIndex: number) => (
                              <Box key={milestone._id} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, pl: 2 }}>
                                  {subscription.name} - {t('Milestone')} {milestoneIndex + 1}
                                </Typography>
                                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                  <colgroup>
                                    <col style={{ width: '40%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '25%' }} />
                                    <col style={{ width: '25%' }} />
                                  </colgroup>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell sx={{ border: 0, pl: 4, verticalAlign: 'middle' }}>
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                          sx={{ ml: 2 }}
                                        >
                                          {t('Date')}:
                                          {moment(milestone.paymentDate).utc().format('L')}
                                          {' | '}
                                          {t('Status')}:{t(milestone.status)}
                                          <Label
                                            sx={{ ml: 2 }}
                                            color={
                                              milestone.orderItem?.orderItemType ===
                                              ChoicesOrderItemType.newResource
                                                ? 'primary'
                                                : 'secondary'
                                            }
                                            variant="outlined"
                                          >
                                            {t(
                                              ChoicesOrderItemLabel[
                                                milestone.orderItem?.orderItemType
                                              ]
                                            )}
                                          </Label>
                                        </Typography>
                                      </TableCell>
                                      <TableCell sx={{ border: 0, verticalAlign: 'middle' }}>
                                        {}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        sx={{ border: 0, verticalAlign: 'middle' }}
                                      >
                                        <Typography variant="body2">
                                          {milestone.orderItem.quantity || 0}
                                        </Typography>
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        sx={{ border: 0, verticalAlign: 'middle' }}
                                      >
                                        <Typography variant="body2">
                                          $ {milestone.total?.toFixed(2) || 0}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              }
            )}
          </TableBody>
        </Table>

        {isAmountMismatch && (
          <Typography variant="body2" color="error" sx={{ mt: 1, px: 2 }}>
            {t('The total amount does not match the OC amount')}
          </Typography>
        )}

        <ShowIf condition={watchAllFields?.legalEntity?.length > 1}>
          <Typography variant="h6" align="right" sx={{ fontWeight: 'bold', p: 2 }}>
            {t('Accumulated amount:')}
            {' $'}
            {totalAmount.toFixed(2)}
          </Typography>
        </ShowIf>

        <Box sx={{ px: 2, pb: 2 }}>
          <OrderOVInformation
            documents={watchAllFields?.legalEntity?.[legalIndex]?.documents || []}
            onDeleteDocument={(document: IOrderDocument) => handleDeleteDocument(document._id)}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
